'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { ImageOff } from 'lucide-react';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useThemeColor } from '@/hooks/useThemeColor';
import { MAGAZINE_SALE_STATUS_LIST } from '@/constants/constants';
import { Magazine, MagazineHubType, MagazineSaleStatus } from '@/types/magazines';
import { TaxRate } from '@/types/taxRate';
import { formatMagazinePrice } from '@/utils/formatters/priceFormatter';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';
import ImageZoom from '@/components/modal/ImageZoom';

export interface MagazineListUnitProps {
    data: Magazine;
    taxRates: TaxRate[] | null;
    hubType?: MagazineHubType;
    seriesStepLabel?: string;
}

export default function MagazineListUnit({ data, taxRates, hubType = 'spot', seriesStepLabel }: MagazineListUnitProps) {
    const { scheme } = useThemeColor();
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const { safePush } = useSafeNavigation();
    const hasEyecatch = Boolean(data?.eyecatch_public_url);

    const getMagazineDetailPath = (modelNumber: string) => {
        if (hubType === 'stage') {
            return `/magazines/dive/stage/detail/${encodeURIComponent(modelNumber)}`;
        }

        return `/magazines/dive/detail/${encodeURIComponent(modelNumber)}`;
    };

    const handleClick = () => {
        if (data.has_detail && data.model_number) {
            safePush(getMagazineDetailPath(data.model_number));
        } else {
            setIsZoomOpen(true);
        }
    };

    const taxRate = taxRates?.find((rate) => rate.tax_category === data.tax_category);
    const saleStatusLabel = MAGAZINE_SALE_STATUS_LIST.find((item) => item.value === data.sale_status)?.label;

    return (
        <>
            <div className={`relative ${seriesStepLabel ? 'pt-5' : 'pt-0'}`} onClick={handleClick}>
                {seriesStepLabel && (
                    <div className="absolute top-0 left-1 -translate-y-1/2 z-10">
                        <span
                            className={`inline-flex items-center rounded-md ${scheme.bg} px-3 py-1 font-corp font-bold text-sm tracking-wider text-color-white-base shadow-sm`}
                        >
                            {seriesStepLabel}巻
                        </span>
                    </div>
                )}

                <div className="flex w-full pb-2 rounded-none md:rounded-xl bg-color-white-base md:shadow-full cursor-pointer">
                    <motion.div className="flex flex-col w-full relative" whileHover="hovered" initial="initial">
                        <div className="relative w-full aspect-[600/734] px-2 pt-2">
                            {hasEyecatch ? (
                                <motion.img
                                    src={data.eyecatch_public_url}
                                    variants={{
                                        initial: { scale: 1 },
                                        hovered: { scale: 1.01 },
                                    }}
                                    transition={{ type: 'spring', stiffness: 300 }}
                                    className="w-full h-full rounded-lg"
                                />
                            ) : (
                                <div className="flex items-center justify-center w-full h-full rounded-lg bg-color-sl-cg-no-license-agreement-bg">
                                    <IconAdaptive
                                        Icon={ImageOff}
                                        strokeWidth={1.5}
                                        baseSize={30}
                                        breakpoints={{ md: 50 }}
                                        className="text-color-co-gray-dark"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-full px-2 pt-3">
                            <div className="flex items-center">
                                <div className="flex-grow">
                                    {data.title_prefix && (
                                        <div className="font-corp font-bold text-sm text-color-base-layout-logo tracking-widest line-clamp-2 leading-tight flex">
                                            <TruncateText
                                                text={data.title_prefix}
                                                maxLength={50}
                                                breakpoints={{ md: 50 }}
                                            />
                                        </div>
                                    )}
                                    <div className="font-corp font-bold text-lg md:text-xl text-color-base-layout-logo tracking-widest line-clamp-2 leading-tight flex">
                                        <TruncateText text={data.title} maxLength={50} breakpoints={{ md: 50 }} />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col text-sm text-color-base mt-2">
                                {data.sale_status === MagazineSaleStatus.on_sale && (
                                    <div className="font-corp text-xs text-color-co-gray-dark space-y-1">
                                        <div className="font-corp text-xs text-color-co-gray-dark space-y-1">
                                            {data.supervisor_name && (
                                                <div className="inline-block px-2.5 py-0.5 rounded-full bg-color-base text-color-white-base mb-3">
                                                    監修：{data.supervisor_name}
                                                </div>
                                            )}
                                            {data.sale_status == MagazineSaleStatus.on_sale && data.price != null && (
                                                <div className="font-corp text-color-base text-sm">
                                                    {formatMagazinePrice(data.price, taxRate)}
                                                </div>
                                            )}
                                            <div className="font-notojp font-medium text-xs tracking-wide">
                                                {<p>形式：{data.magazine_format}</p>}
                                                {data.total_pages && <p>総ページ数：{data.total_pages}</p>}
                                                {data.jan_code && <p>JANコード：{data.jan_code}</p>}
                                                {data.model_number && <p>型番：{data.model_number}</p>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {data.sale_status !== MagazineSaleStatus.on_sale && (
                                    <div className="inline-block">
                                        <span
                                            className={`
                                            px-2 py-0.5 rounded-full text-xs font-corp
                                            ${
                                                data.sale_status === MagazineSaleStatus.pre_sale
                                                    ? 'bg-color-co-gray-light text-color-co-gray-dark'
                                                    : 'bg-color-co-gray-light text-color-co-gray-dark'
                                            }
                                        `}
                                        >
                                            {saleStatusLabel}
                                        </span>
                                    </div>
                                )}
                                <div className="font-corp pt-2">{data.content}</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
                <ImageZoom
                    isOpen={isZoomOpen}
                    onClose={() => setIsZoomOpen(false)}
                    src={data.eyecatch_public_url ?? '/images/magazine/eyecatch_default.png'}
                    alt={`${data.title_prefix ? data.title_prefix + ' ' : ''}${data.title}`}
                />
            </div>
        </>
    );
}
